import React, { useState, useRef, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import DetailtText from "./DetailText";
import { Player } from "@lottiefiles/react-lottie-player";
import { FeatureModuleDetailType } from "../types/FeatureModuleDetail";

const ModuleDetailDesktop = ({ feature, onClick }: FeatureModuleDetailType) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  const [isAnimationStarted, setIsAnimationStarted] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const horizontalRef = useRef(null);
  const heightRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<Player>(null);

  const handleScroll = () => {
    if (!containerRef?.current || !horizontalRef.current || !heightRef.current)
      return;
    const offsetTop = -containerRef.current.offsetTop;

    const currentOffset = Math.abs(offsetTop) > 40;

    if (currentOffset) {
      setIsAnimationStarted(true);
      playerRef.current?.play();
    }

    if (containerRef.current.offsetTop <= 0) {
      setIsAnimationStarted(false);
    }

    setTranslateY(!currentOffset ? 0 : offsetTop + 55);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [containerRef, heightRef, horizontalRef, isAnimationStarted]);

  const activeItem = useMemo(() => {
    return feature?.[activeIndex];
  }, [feature, activeIndex]);

  return (
    <div
      className={`feature-module md:hidden feature-module--desktop relative w-full transition-all duration-500 ease-linear redesign-component`}
      style={{ height: `${100 * feature?.length + 5}vh` }}
      ref={heightRef}
    >
      <div
        className={`${
          !isAnimationStarted ? "py-[40px] mx-20 lg:mx-10" : "mx-0 py-0"
        } h-full transition-all duration-500 ease-linear`}
      >
        <div
          className={`w-full flex items-center justify-center sticky top-[88px] h-[calc(100vh-88px)] overflow-hidden`}
          ref={containerRef}
        >
          <div
            className={`h-full flex justify-center items-center px-[40px] w-2/4 ${
              !isAnimationStarted ? "rounded-l-[46px]" : "rounded-none"
            }`}
            style={{
              backgroundColor: activeItem?.backgroundColor,
            }}
          >
            <div className="flex justify-center items-center">
              <AnimatePresence exitBeforeEnter>
                {activeItem?.desktopTabletAsset?.lottie ? (
                  <motion.div
                    key={activeItem?.desktopTabletAsset?.lottie}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{
                      opacity: { duration: 0.4 },
                    }}
                    className="w-full max-w-[540px] max-h-[680px]"
                  >
                    <Player
                      keepLastFrame
                      autoplay={false}
                      ref={playerRef}
                      loop={activeItem?.desktopTabletAsset?.lottieLoop}
                      src={activeItem?.desktopTabletAsset?.lottie}
                    />
                  </motion.div>
                ) : (
                  <motion.img
                    key={activeItem?.desktopTabletAsset?.image}
                    src={activeItem?.desktopTabletAsset?.image}
                    alt={activeItem?.desktopTabletAsset?.imageAltText}
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{
                      opacity: { duration: 0.4 },
                      y: { duration: 0.4, ease: "easeOut" },
                    }}
                    className="w-full max-w-[540px] max-h-[680px]"
                  />
                )}
              </AnimatePresence>
            </div>
          </div>
          <div
            className={`w-2/4 h-screen`}
            ref={horizontalRef}
            style={{
              transform: `translateY(${translateY}px)`,
            }}
          >
            {feature?.map((item, index) => {
              return (
                <DetailtText
                  key={index}
                  index={index}
                  item={item}
                  setActiveIndex={setActiveIndex}
                  onClick={onClick}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModuleDetailDesktop;
